import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './auth';
import { LocaleProvider } from './i18n';
import { api } from './api';
import { createQueryClient } from './queryClient';
import { setupAppUpdate } from './appUpdate';
import { deliveriesSending } from './deliveryQueue';
// Self-hosted fonts (the PWA works offline; never Google Fonts at runtime). Weights per the design system.
import '@fontsource/cinzel/500.css';
import '@fontsource/cinzel/600.css';
import '@fontsource/jost/400.css';
import '@fontsource/jost/500.css';
import '@fontsource/jost/600.css';
import '@fontsource/josefin-sans/300-italic.css';
import './styles.css';

const queryClient = createQueryClient();
const root = document.getElementById('root');
if (!root) throw new Error('index.html is missing #root');

// Errors the browser hits go to the System screen (best effort; never loops on its own failure).
let reported = 0;
const report = (message: string, detail?: string) => {
  if (reported++ > 5) return;
  void api.reportClientError({ message: message.slice(0, 500), ...(detail ? { detail: detail.slice(0, 4000) } : {}), path: window.location.pathname.replace(/^\/d\/.+/, '/d/:token'), version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev' }).catch(() => undefined);
};
window.addEventListener('error', (e) => report(e.message || 'error', e.error instanceof Error ? e.error.stack : undefined));
window.addEventListener('unhandledrejection', (e) => report(e.reason instanceof Error ? e.reason.message : String(e.reason), e.reason instanceof Error ? e.reason.stack : undefined));

// the app kept on the device for no signal, and kept up to date (JL, 2026-11-01)
setupAppUpdate(() => queryClient.isMutating() > 0 || deliveriesSending());

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </LocaleProvider>
    </QueryClientProvider>
  </StrictMode>,
);
